import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const FloatingImageGallery = ({ images = [] }) => {
  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  // Staggered animation for each image
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };

  const itemVariants = {
    hidden: { y: 60, opacity: 0, scale: 0.8 },
    visible: {
      y: 0,
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.8,
        ease: [0.6, 0.05, 0.01, 0.9]
      }
    }
  };

  // Floating animation
  const floatAnimation = {
    y: [0, -15, 0],
    transition: {
      duration: 3,
      repeat: Infinity,
      ease: "easeInOut"
    }
  };

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={inView ? "visible" : "hidden"}
      variants={containerVariants}
      className="grid grid-cols-1 md:grid-cols-3 gap-8"
    >
      {images.map((image, index) => (
        <motion.div
          key={index}
          variants={itemVariants}
          animate={inView ? floatAnimation : {}}
          style={{ animationDelay: `${index * 0.2}s` }}
          className="relative group cursor-pointer"
        >
          <motion.div
            className="relative overflow-hidden aspect-square"
            whileHover={{ 
              scale: 1.05,
              rotate: index % 2 === 0 ? 3 : -3,
              zIndex: 10
            }}
            transition={{ duration: 0.4 }}
          >
            <img
              src={image.url}
              alt={image.alt}
              className="w-full h-full object-cover"
            />
            
            {/* Overlay with content */}
            <div className="absolute inset-0 bg-gradient-to-t from-navy-dark via-navy-dark/50 to-transparent opacity-80 group-hover:opacity-40 transition-opacity duration-500" />
            
            {/* Caption */}
            {image.caption && (
              <div className="absolute bottom-0 left-0 right-0 p-6 transform translate-y-4 group-hover:translate-y-0 transition-transform duration-500">
                <h4 className="text-white font-bold text-lg mb-1">{image.caption}</h4>
                {image.description && (
                  <p className="text-gray-300 text-sm opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                    {image.description}
                  </p>
                )}
              </div>
            )}

            {/* Corner accent */}
            <div className="absolute top-4 right-4 w-12 h-12 border-t-2 border-r-2 border-white opacity-0 group-hover:opacity-50 transition-opacity duration-500" />
          </motion.div>
        </motion.div>
      ))}
    </motion.div>
  );
};

export default FloatingImageGallery;
